import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Typography,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Dashboard,
  Description,
  List as ListIcon,
  Settings,
  BarChart,
  CreateNewFolder,
  Archive as ArchiveIcon,
  DeleteOutline as DeleteIcon,
  ExpandLess,
  ExpandMore,
  TableChart as TableChartIcon,
  TableRows as TableRowsIcon,
  Nature,
  Calculate,
  Assessment,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../styles/Sidebar.css';

const Sidebar = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isFormUser, setIsFormUser] = useState(false);
  
  // Menü bölümlerinin açılma/kapanma durumları - önce kapalı gelecek
  const [openSections, setOpenSections] = useState({
    mainMenu: false,
    formManagement: false,
    tableManagement: false,
    carbonFootprint: false,
    fileManagement: false,
    settings: false,
  });

  // Form kullanıcısı kontrolü
  useEffect(() => {
    const formUserToken = localStorage.getItem('formUserToken');
    const formUserData = localStorage.getItem('formUserData');
    
    if (formUserToken && formUserData) {
      try {
        const parsedUserData = JSON.parse(formUserData);
        if (parsedUserData && parsedUserData.id) {
          setIsFormUser(true);
        }
      } catch (error) {
        console.error('Error parsing stored form user data:', error);
      }
    }
  }, []);

  // Form kullanıcısı giriş yapmışsa sidebar'ı gösterme
  if (isFormUser) {
    return null;
  }

  const menuItems = [
    { text: t('home'), path: '/', icon: <Dashboard /> },
  ];

  const formItems = [
    { text: t('create_form'), path: '/form-builder', icon: <Description /> },
    { text: t('my_forms'), path: '/forms', icon: <ListIcon /> },
    { text: t('responses'), path: '/answers', icon: <BarChart /> },
  ];

  const tableItems = [
    { text: 'Tablo Oluştur', path: '/spreadsheet', icon: <TableRowsIcon /> },
    { text: 'Kaydedilen Tablolar', path: '/tables', icon: <TableChartIcon /> },
  ];

  const carbonItems = [
    { text: 'Karbon Ayak İzi', path: '/carbon-calculator', icon: <Calculate /> },
  /*   { text: 'Emisyon Faktörleri', path: '/emission-factors-admin', icon: <Nature /> },
    { text: 'Sürdürülebilirlik Raporu', path: '/sustainability-reports', icon: <Assessment /> }, */
  ];

  const fileItems = [
    { text: t('create_folder'), path: '/folders', icon: <CreateNewFolder /> },
    { text: t('archive'), path: '/archived-forms', icon: <ArchiveIcon /> },
    { text: t('deleted_items'), path: '/deleted-items', icon: <DeleteIcon /> },
  ];

/*   const settingsItems = [
    { text: t('settings'), path: '/settings', icon: <Settings /> },
  ]; */

  // Menü bölümünü açma/kapama fonksiyonu
  const handleSectionToggle = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const ListItemComponent = ({ item, index }) => {
    const handleItemClick = () => {
      navigate(item.path);
      onClose();
    };

    return (
      <Tooltip 
        title={item.text} 
        placement="right"
        arrow
        enterDelay={200}
        leaveDelay={0}
      >
        <ListItem
          onClick={handleItemClick}
          className={`menu-item ${location.pathname === item.path ? 'selected-item' : ''}`}
          style={{ '--item-index': index }}
        >
          <ListItemIcon className="menu-icon">
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            className="menu-text"
          />
        </ListItem>
      </Tooltip>
    );
  };

  // Menü bölümü başlığı bileşeni
  const SectionHeader = ({ title, section, children }) => (
    <Box>
      <ListItem
        onClick={() => handleSectionToggle(section)}
        sx={{
          cursor: 'pointer',
          borderRadius: '8px',
          mx: 1,
          my: 0.5,
          transition: 'all 0.3s ease',
          background: openSections[section] 
            ? 'linear-gradient(135deg, rgba(65, 88, 208, 0.1) 0%, rgba(200, 80, 192, 0.1) 100%)'
            : 'transparent',
          border: openSections[section] 
            ? '1px solid rgba(65, 88, 208, 0.2)'
            : '1px solid transparent',
          '&:hover': {
            background: openSections[section]
              ? 'linear-gradient(135deg, rgba(65, 88, 208, 0.15) 0%, rgba(200, 80, 192, 0.15) 100%)'
              : 'linear-gradient(135deg, rgba(65, 88, 208, 0.05) 0%, rgba(200, 80, 192, 0.05) 100%)',
            transform: 'translateX(4px)',
            boxShadow: '0 4px 12px rgba(65, 88, 208, 0.15)',
          }
        }}
      >
        <ListItemText
          primary={
            <Typography 
              sx={{
                color: openSections[section] ? '#4158D0' : '#475569',
                fontWeight: openSections[section] ? 600 : 500,
                fontSize: '0.875rem',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                transition: 'all 0.3s ease'
              }}
            >
              {title}
            </Typography>
          }
        />
        <IconButton 
          size="small"
          sx={{
            color: openSections[section] ? '#4158D0' : '#64748b',
            transition: 'all 0.3s ease',
            '&:hover': {
              color: '#C850C0',
              transform: 'scale(1.1)',
            }
          }}
        >
          {openSections[section] ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </ListItem>
      <Collapse in={openSections[section]} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {children}
        </List>
      </Collapse>
    </Box>
  );

  return (
    <Drawer
      variant="temporary"
      open={isOpen}
      onClose={onClose}
      className="sidebar"
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
          border: 'none',
          height: '100%',
          top: 0,
          zIndex: 1200
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.2)'
        }
      }}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <Box 
        sx={{ 
          height: '100vh',
          pt: '64px',
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(180deg, #fafbfc 0%, #f5f7fa 100%)',
          boxShadow: '4px 0 16px rgba(65, 88, 208, 0.08)',
          borderRight: '1px solid rgba(65, 88, 208, 0.1)'
        }}
      >
                 <Box sx={{ 
           flex: 1,
           overflow: 'auto',
          '& .menu-item': {
            mx: 1,
            my: 0.5,
            borderRadius: '12px',
            transition: 'all 0.3s ease',
            border: '1px solid transparent',
            background: 'transparent',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(65, 88, 208, 0.08) 0%, rgba(200, 80, 192, 0.08) 100%)',
              transform: 'translateX(6px)',
              boxShadow: '0 4px 12px rgba(65, 88, 208, 0.1)',
              border: '1px solid rgba(65, 88, 208, 0.15)',
            }
          },
          '& .selected-item': {
            background: 'linear-gradient(135deg, rgba(65, 88, 208, 0.12) 0%, rgba(200, 80, 192, 0.12) 100%)',
            border: '1px solid rgba(65, 88, 208, 0.25)',
            boxShadow: '0 4px 12px rgba(65, 88, 208, 0.15)',
            '& .menu-icon': {
              color: '#4158D0'
            },
            '& .menu-text': {
              color: '#4158D0',
              fontWeight: 600
            }
          },
          '& .menu-icon': {
            minWidth: '40px',
            color: '#64748b',
            transition: 'all 0.3s ease'
          },
          '& .menu-text': {
            color: '#475569',
            fontSize: '0.95rem',
            fontWeight: 500,
            transition: 'all 0.3s ease'
          },
          '& .divider': {
            my: 2,
            mx: 2,
            borderColor: 'rgba(65, 88, 208, 0.15)',
            borderWidth: '1px',
            opacity: 0.6
          }
        }}>
          
          {/* ANA MENÜ BÖLÜMÜ */}
          <SectionHeader title={t('main_menu')} section="mainMenu">
            {menuItems.map((item, index) => (
              <ListItemComponent key={item.text} item={item} index={index} />
            ))}
          </SectionHeader>

          <Divider className="divider" />
          
          {/* FORM YÖNETİMİ BÖLÜMÜ */}
          <SectionHeader title="FORM YÖNETİMİ" section="formManagement">
            {formItems.map((item, index) => (
              <ListItemComponent 
                key={item.text} 
                item={item} 
                index={index + menuItems.length} 
              />
            ))}
          </SectionHeader>

          <Divider className="divider" />
          
          
          
                     {/* TABLO YÖNETİMİ BÖLÜMÜ */}
           <SectionHeader title="TABLO YÖNETİMİ" section="tableManagement">
             {tableItems.map((item, index) => (
               <ListItemComponent 
                 key={item.text} 
                 item={item} 
                 index={index + menuItems.length + formItems.length} 
               />
             ))}
           </SectionHeader>

           <Divider className="divider" />
           
           {/* KARBON AYAK İZİ BÖLÜMÜ */}
           <SectionHeader title="SÜRDÜRÜLEBİLİRLİK" section="carbonFootprint">
             {carbonItems.map((item, index) => (
               <ListItemComponent 
                 key={item.text} 
                 item={item} 
                 index={index + menuItems.length + formItems.length + tableItems.length} 
               />
             ))}
           </SectionHeader>

           <Divider className="divider" />
           
           {/* DOSYA YÖNETİMİ BÖLÜMÜ */}
           <SectionHeader title="DOSYA YÖNETİMİ" section="fileManagement">
             {fileItems.map((item, index) => (
               <ListItemComponent 
                 key={item.text} 
                 item={item} 
                 index={index + menuItems.length + formItems.length + tableItems.length + carbonItems.length} 
               />
             ))}
           </SectionHeader>

           <Divider className="divider" />

          

       
          
          {/* AYARLAR BÖLÜMÜ */}
                 {/*   <SectionHeader title={t('settings')} section="settings">
             {settingsItems.map((item, index) => (
               <ListItemComponent 
                 key={item.text} 
                 item={item} 
                 index={index + menuItems.length + formItems.length + tableItems.length + fileItems.length} 
               />
             ))}
           </SectionHeader> */}
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;